import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilSimple, Trash, Plus, UploadSimple, X, CalendarCheck, WhatsappLogo } from "@phosphor-icons/react";

const TIPOS = ["Estande Contêiner", "Loja Satélite/Âncora", "VM Corporate", "Gastronomia"];
const POSICOES = ["Pátio Interno", "Fachada", "1º Piso", "2º Piso"];
const STATUS = ["Disponível", "Em Negociação", "Alugado"];

const STATUS_STYLE = {
  "Disponível": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "Em Negociação": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "Alugado": "bg-red-500/15 text-red-400 border-red-500/30",
};

const empty = {
  nome_unidade: "", tipo: "Estande Contêiner", metragem_m2: 0, posicao: "Pátio Interno",
  valor_aluguel: 0, estimativa_condominio: 0, status: "Disponível",
  fotos_urls: [], link_tour_360: "", observacoes: "",
};

const inp = "w-full bg-vm-muted border border-vm-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-vm-gold/50 text-vm-fg";
const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

export function UnidadesManager() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/unidades"); setList(data); }
    catch { toast.error("Erro ao carregar unidades."); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u }); setOpen(true); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, fotos_urls: [...(f.fotos_urls || []), data.url] }));
      toast.success("Foto enviada.");
    } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload."); }
    finally { setUploading(false); }
  };

  const removePhoto = (url) => setForm((f) => ({ ...f, fotos_urls: f.fotos_urls.filter((u) => u !== url) }));

  const save = async () => {
    if (!form.nome_unidade.trim()) { toast.error("Informe o nome da unidade."); return; }
    const payload = {
      ...form,
      metragem_m2: Number(form.metragem_m2) || 0,
      valor_aluguel: Number(form.valor_aluguel) || 0,
      estimativa_condominio: Number(form.estimativa_condominio) || 0,
    };
    try {
      if (editing) await api.put(`/admin/unidades/${editing.id}`, payload);
      else await api.post("/admin/unidades", payload);
      toast.success(editing ? "Unidade atualizada." : "Unidade cadastrada.");
      setOpen(false); load();
    } catch { toast.error("Erro ao salvar unidade."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta unidade?")) return;
    try { await api.delete(`/admin/unidades/${id}`); toast.success("Removida."); load(); }
    catch { toast.error("Erro ao remover."); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl">Inventário de Unidades</h2>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-vm-gold text-vm-bg font-medium rounded-full px-5 py-2.5 text-sm" data-testid="add-unidade-btn">
          <Plus size={18} weight="bold" /> Nova Unidade
        </button>
      </div>

      <div className="grid gap-3">
        {list.length === 0 && <p className="text-vm-mutedFg text-sm">Nenhuma unidade cadastrada ainda.</p>}
        {list.map((u) => (
          <div key={u.id} className="flex items-center gap-4 bg-vm-surface border border-vm-border rounded-xl p-3" data-testid={`admin-unidade-${u.id}`}>
            {u.fotos_urls?.[0] ? (
              <img src={resolveImg(u.fotos_urls[0])} alt={u.nome_unidade} className="h-16 w-16 rounded-lg object-cover bg-vm-muted" />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-vm-muted flex items-center justify-center text-vm-mutedFg text-[10px]">sem foto</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{u.nome_unidade}</span>
                <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_STYLE[u.status] || ""}`}>{u.status}</span>
              </div>
              <p className="text-xs text-vm-mutedFg mt-0.5 truncate">
                {u.tipo} · {u.metragem_m2}m² · {u.posicao || "-"} · Aluguel {brl(u.valor_aluguel)} · Cond. {brl(u.estimativa_condominio)}
                {u.fotos_urls?.length ? ` · ${u.fotos_urls.length} foto(s)` : ""}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => openEdit(u)} className="p-2 text-vm-mutedFg hover:text-vm-gold" data-testid={`edit-unidade-${u.id}`}><PencilSimple size={18} /></button>
              <button onClick={() => remove(u.id)} className="p-2 text-vm-mutedFg hover:text-red-400" data-testid={`delete-unidade-${u.id}`}><Trash size={18} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-vm-surface border-vm-border text-vm-fg max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Editar Unidade" : "Nova Unidade"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Nome da Unidade</label>
              <input className={inp} value={form.nome_unidade} onChange={(e) => set("nome_unidade", e.target.value)} placeholder="Ex: Estande 04, Loja 12, Sala Corporate 02" data-testid="unidade-nome" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-1.5">Tipo</label>
                <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                  <SelectTrigger className={inp} data-testid="unidade-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-vm-muted border-vm-border text-vm-fg">{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-1.5">Posição</label>
                <Select value={form.posicao} onValueChange={(v) => set("posicao", v)}>
                  <SelectTrigger className={inp} data-testid="unidade-posicao"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-vm-muted border-vm-border text-vm-fg">{POSICOES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Metragem (m²)</label>
                <input type="number" className={inp} value={form.metragem_m2} onChange={(e) => set("metragem_m2", e.target.value)} data-testid="unidade-metragem" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Aluguel (R$)</label>
                <input type="number" className={inp} value={form.valor_aluguel} onChange={(e) => set("valor_aluguel", e.target.value)} data-testid="unidade-aluguel" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Condomínio (R$)</label>
                <input type="number" className={inp} value={form.estimativa_condominio} onChange={(e) => set("estimativa_condominio", e.target.value)} data-testid="unidade-condominio" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-1.5">Status de Disponibilidade</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className={inp} data-testid="unidade-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-vm-muted border-vm-border text-vm-fg">{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Link do Tour Virtual 360°</label>
              <input className={inp} value={form.link_tour_360} onChange={(e) => set("link_tour_360", e.target.value)} placeholder="https://..." data-testid="unidade-tour" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-2">Fotos da Unidade</label>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {(form.fotos_urls || []).map((url) => (
                  <div key={url} className="relative h-16 w-16 rounded-lg overflow-hidden border border-vm-border group">
                    <img src={resolveImg(url)} alt="foto" className="h-full w-full object-cover" />
                    <button onClick={() => removePhoto(url)} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-foto"><X size={12} weight="bold" /></button>
                  </div>
                ))}
                <label className="inline-flex items-center gap-2 cursor-pointer border border-vm-border rounded-lg h-16 px-4 text-sm hover:border-vm-gold/50 transition-colors">
                  <UploadSimple size={16} /> {uploading ? "Enviando..." : "Foto"}
                  <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="unidade-upload" />
                </label>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Observações</label>
              <textarea rows={2} className={`${inp} resize-none`} value={form.observacoes} onChange={(e) => set("observacoes", e.target.value)} data-testid="unidade-obs" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} className="bg-vm-gold text-vm-bg font-semibold rounded-full px-6 py-2.5 text-sm w-full" data-testid="unidade-save">Salvar Unidade</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AgendamentosList() {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/admin/agendamentos").then((r) => setList(r.data)).catch(() => {});
  }, []);

  const waLink = (a) => {
    const text = `Olá! Retornando sobre a visita agendada.\n*Nome:* ${a.nome}\n*Segmento:* ${a.segmento}\n*Unidade de Interesse:* ${a.unidade_interesse}\n*Data da Visita:* ${a.data_visita} às ${a.horario}`;
    return `https://wa.me/5521987356892?text=${encodeURIComponent(text)}`;
  };

  return (
    <div>
      <h2 className="font-serif text-2xl mb-6">Agendamentos de Visita ({list.length})</h2>
      {list.length === 0 && <p className="text-vm-mutedFg text-sm">Nenhum agendamento registrado ainda. O Secretário Carlos registra automaticamente quando conclui um agendamento no chat.</p>}
      <div className="grid gap-3">
        {list.map((a) => (
          <div key={a.id} className="bg-vm-surface border border-vm-border rounded-xl p-4 flex items-start gap-4" data-testid={`agendamento-${a.id}`}>
            <div className="h-10 w-10 rounded-full bg-vm-gold/15 text-vm-gold flex items-center justify-center shrink-0"><CalendarCheck size={20} /></div>
            <div className="flex-1 min-w-0">
              <p className="font-medium">{a.nome} <span className="text-vm-mutedFg text-sm font-normal">· {a.segmento || "—"}</span></p>
              <p className="text-sm text-vm-mutedFg mt-1">Interesse: <span className="text-vm-fg">{a.unidade_interesse || "—"}</span></p>
              <p className="text-sm text-vm-mutedFg">Visita: <span className="text-vm-fg">{a.data_visita || "—"} {a.horario ? `às ${a.horario}` : ""}</span></p>
            </div>
            <a href={waLink(a)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white text-xs font-semibold px-3.5 py-2 rounded-full shrink-0 hover:brightness-105" data-testid={`agendamento-wa-${a.id}`}>
              <WhatsappLogo size={15} weight="fill" /> WhatsApp
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
